import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, Save, ClipboardCheck, AlertTriangle } from 'lucide-react';

export default function PresencaPage() {
  const [treinos, setTreinos] = useState([]);
  const [allTreinos, setAllTreinos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [selectedTreino, setSelectedTreino] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    Promise.all([api.get('/gestor/treinos'), api.get('/gestor/turmas')])
      .then(([t, tu]) => { setAllTreinos(t.data); setTurmas(tu.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const filtered = allTreinos.filter(t => {
      const treinoDate = t.data?.split('T')[0] || t.data;
      return treinoDate === selectedDate;
    });
    setTreinos(filtered);
    setSelectedTreino(null);
    setAlunos([]);
  }, [selectedDate, allTreinos]);

  const loadAlunos = async (treinoId) => {
    setLoading(true);
    try {
      const res = await api.get(`/gestor/presenca/alunos-aptos/${treinoId}`);
      setAlunos(res.data);
    } catch { toast.error('Erro ao carregar alunos'); }
    setLoading(false);
  };

  const handleTreinoSelect = (treinoId) => {
    const treino = treinos.find(t => t.id === treinoId);
    setSelectedTreino(treino);
    loadAlunos(treinoId);
  };

  const togglePresenca = (alunoId) => {
    setAlunos(prev => prev.map(a => a.id === alunoId ? { ...a, presente: a.presente === true ? false : true } : a));
  };

  const markAll = (value) => {
    setAlunos(prev => prev.map(a => a.apto ? { ...a, presente: value } : a));
  };

  const salvarPresenca = async () => {
    if (!selectedTreino) return;
    setSaving(true);
    try {
      const presencas = alunos.filter(a => a.apto).map(a => ({ aluno_id: a.id, presente: a.presente === true }));
      await api.post('/gestor/presenca/marcar', { treino_id: selectedTreino.id, presencas });
      toast.success('Presença registrada com sucesso');
    } catch { toast.error('Erro ao salvar presença'); }
    setSaving(false);
  };

  const getTurmaName = (id) => turmas.find(t => t.id === id)?.nome || '-';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="presenca-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">CONTROLE DE PRESENÇA</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Data</Label>
          <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-[#0A0A0A] border-[#27272A]" data-testid="presenca-date" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Treino</Label>
          <Select value={selectedTreino?.id || ''} onValueChange={handleTreinoSelect}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="presenca-treino-select">
              <SelectValue placeholder={treinos.length === 0 ? 'Nenhum treino nesta data' : 'Selecionar treino'} />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#27272A]">
              {treinos.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.nome} - {t.horario} ({getTurmaName(t.turma_id)})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedTreino && (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-[#007AFF]" />
                {selectedTreino.nome} - {getTurmaName(selectedTreino.turma_id)}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => markAll(true)} className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]" data-testid="mark-all-present">Todos Presente</Button>
                <Button size="sm" variant="outline" onClick={() => markAll(false)} className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]" data-testid="mark-all-absent">Todos Ausente</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" /></div>
            ) : alunos.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">Nenhum aluno vinculado a esta turma</p>
            ) : (
              <div className="space-y-2">
                {alunos.map(a => (
                  <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
                    !a.apto ? 'border-red-500/20 bg-red-500/5 opacity-60' :
                    a.presente === true ? 'border-green-500/20 bg-green-500/5' :
                    a.presente === false ? 'border-red-500/20 bg-red-500/5' :
                    'border-[#27272A] bg-[#0A0A0A]'
                  }`} data-testid={`presenca-aluno-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={a.presente === true}
                        onCheckedChange={() => a.apto && togglePresenca(a.id)}
                        disabled={!a.apto}
                        className="border-[#27272A] data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]"
                        data-testid={`presenca-check-${a.id}`}
                      />
                      <div>
                        <p className="text-sm font-medium text-white">{a.name}</p>
                        <p className="text-xs text-zinc-500">{a.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!a.apto && (
                        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Inapto
                        </Badge>
                      )}
                      {a.presente === true && <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Presente</Badge>}
                      {a.presente === false && <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Ausente</Badge>}
                    </div>
                  </div>
                ))}
                <div className="pt-4">
                  <Button onClick={salvarPresenca} disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD] w-full sm:w-auto" data-testid="save-presenca-button">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar Presença
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
